import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, ListChecks, Plus } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";

/** Asistente IA por paso: chat persistente + generación de plan de acción. */
export default function StepAssistant({ stepCode, canWrite, onTasksAdded }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [planItems, setPlanItems] = useState(null);
  const [selected, setSelected] = useState({});
  const [planning, setPlanning] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/journey/steps/${stepCode}/assistant`);
        if (!cancelled) setMessages(data.messages || []);
      } catch { /* sin historial */ }
    })();
    return () => { cancelled = true; };
  }, [stepCode]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setInput("");
    try {
      const { data } = await api.post(`/journey/steps/${stepCode}/assistant/message`, { content: text }, { timeout: 120000 });
      setMessages((m) => [...m, data.message]);
    } catch (err) {
      // Recuperar el hilo si el proxy cortó pero el servidor respondió
      try {
        const { data } = await api.get(`/journey/steps/${stepCode}/assistant`);
        setMessages(data.messages || []);
      } catch { /* ignore */ }
      toast.error(formatApiError(err.response?.data?.detail) || "El asistente tardó demasiado. Inténtalo otra vez.");
    } finally { setSending(false); }
  };

  const generatePlan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post(`/journey/steps/${stepCode}/assistant/plan`, {}, { timeout: 120000 });
      setPlanItems(data.items);
      setSelected(Object.fromEntries(data.items.map((_, i) => [i, true])));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo generar el plan. Inténtalo otra vez."); }
    finally { setPlanning(false); }
  };

  const acceptPlan = async () => {
    const items = planItems.filter((_, i) => selected[i]);
    if (items.length === 0) return toast.error("Marca al menos una acción.");
    try {
      const { data } = await api.post(`/journey/steps/${stepCode}/custom-tasks`, { items, source: "ai_plan" });
      toast.success(`${items.length} acción(es) añadida(s) a tus tareas. Las completas tú.`);
      setPlanItems(null);
      onTasksAdded?.(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="step-assistant">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-[14px] font-semibold text-slate-900">Asistente</span>
      </div>
      <p className="text-[12px] text-slate-500 mb-3">Pregunta lo que quieras sobre este paso o pide un plan. El asistente no aprueba ni completa nada por ti.</p>

      {canWrite && (
        <div className="mb-3">
          <Button variant="outline" size="sm" onClick={generatePlan} disabled={planning} data-testid="assistant-plan-btn">
            {planning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ListChecks className="h-4 w-4 mr-1" />}
            Generar plan de acción
          </Button>
        </div>
      )}

      {planItems && (
        <div className="mb-4 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="assistant-plan">
          <div className="text-[13px] font-medium text-slate-700 mb-2">Plan propuesto · marca las que quieras añadir como tareas</div>
          <div className="space-y-2">
            {planItems.map((it, i) => (
              <label key={i} className="flex items-start gap-2 text-[13px] cursor-pointer" data-testid={`plan-item-${i}`}>
                <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i]: !!v }))} className="mt-0.5" />
                <span>
                  <span className="text-slate-800">{it.title}</span>
                  {it.estimated_effort && <span className="text-[11px] text-slate-400"> · {it.estimated_effort}</span>}
                  {it.detail && <span className="block text-[12px] text-slate-500">{it.detail}</span>}
                </span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={acceptPlan} data-testid="assistant-plan-accept"><Plus className="h-4 w-4 mr-1" /> Añadir como tareas</Button>
            <Button size="sm" variant="ghost" onClick={() => setPlanItems(null)}>Descartar</Button>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="max-h-80 overflow-y-auto space-y-3 mb-3" data-testid="assistant-messages">
        {messages.length === 0 && <p className="text-[13px] text-slate-400 text-center py-4">Aún no has hablado con el asistente. Pregúntale para empezar.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-800"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="bg-slate-100 text-slate-500 rounded-lg px-3 py-2 text-[13px]"><Loader2 className="h-4 w-4 animate-spin inline" /> Pensando...</div></div>}
      </div>

      {canWrite && (
        <div className="flex gap-2 items-end">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Ayúdame con este paso..."
            data-testid="assistant-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <Button onClick={send} disabled={sending || !input.trim()} data-testid="assistant-send"><Send className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
